/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN TOURNAMENT API (PRODUCTION READY)
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   ✅ BASE CONFIG
============================================================= */
const BASE = "/tournament";

const ENDPOINTS = {
  PARTICIPANTS: `${BASE}/tournament_participants.php`,
  TOURNAMENTS: `${BASE}/tournaments.php`, // ✅ NEW,
  EXPORTPARTICIPANTS: `${BASE}/export-participants.php`, // ✅ NEW
  RECEIPT: `${BASE}/receipt/download-receipt.php`, // ✅ NEW
};

const EXPORT_TOURNAMENTS_ENDPOINT = `${BASE}/export-tournaments.php`;
/* =============================================================
   ✅ RESPONSE HANDLER (SAFE)
============================================================= */
const handleResponse = (res, fallbackMessage) => {
  if (!res || res.status !== "success") {
    return {
      success: false,
      message: res?.message || fallbackMessage,
      data: [],
      meta: {},
    };
  }

  return {
    success: true,
    message: res.message || "Success",
    data: Array.isArray(res.data) ? res.data : [],
    meta: res.meta || {},
  };
};

/* =============================================================
   ✅ ERROR HANDLER (CONSISTENT)
============================================================= */
const handleError = (error, label) => {
  console.error(`❌ ${label} Error:`, error);

  return {
    success: false,
    message: error?.message || "Unexpected error occurred",
    data: [],
    meta: {},
  };
};

/* =============================================================
   👥 GET PARTICIPANTS (FILTER + PAGINATION)
============================================================= */
export const getParticipants = async (
  {
    tournament_id,
    page = 1,
    limit = 10,
    category = "",
    gender = "",
    search = "",
  } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ENDPOINTS.PARTICIPANTS,
      null,
      "Fetch Participants",
      {
        params: {
          tournament_id,
          page,
          limit,
          ...(category && { category }),
          ...(gender && { gender }),
          ...(search && { search }),
        },
        ...options,
      }
    );

    return handleResponse(res, "Failed to fetch participants");
  } catch (error) {
    return handleError(error, "Fetch Participants");
  }
};

/* =============================================================
   🔥 ADD PARTICIPANTS (MULTIPLE)
============================================================= */
export const addParticipants = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      {
        action: "ADD", // ✅ backend clarity
        ...payload,
      },
      "Add Participants",
      options
    );

    return handleResponse(res, "Failed to add participants");
  } catch (error) {
    return handleError(error, "Add Participants");
  }
};

/* =============================================================
   🔥 UPDATE PARTICIPANT (SINGLE)
============================================================= */
export const updateParticipant = async (payload, options = {}) => {
  try {
    if (!payload?.id) {
      return {
        success: false,
        message: "Participant ID is required",
        data: [],
        meta: {},
      };
    }

    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      {
        action: "UPDATE", // ✅ important
        ...payload,
      },
      "Update Participant",
      options
    );

    return handleResponse(res, "Failed to update participant");
  } catch (error) {
    return handleError(error, "Update Participant");
  }
};

/* =============================================================
   🔥 DELETE PARTICIPANT
============================================================= */
export const deleteParticipant = async (id, options = {}) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Participant ID is required",
        data: [],
        meta: {},
      };
    }

    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      {
        action: "DELETE", // ✅ backend action
        id,
      },
      "Delete Participant",
      options
    );

    return handleResponse(res, "Failed to delete participant");
  } catch (error) {
    return handleError(error, "Delete Participant");
  }
};

/* =============================================================
   🏆 GET TOURNAMENTS (WITH CATEGORIES)
============================================================= */
export const getTournaments = async (options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      ENDPOINTS.TOURNAMENTS,
      null,
      "Fetch Tournaments",
      options
    );

    // custom handling (because nested categories)
    if (!res || res.status !== "success") {
      return {
        success: false,
        message: res?.message || "Failed to fetch tournaments",
        data: [],
      };
    }

    return {
      success: true,
      message: res.message || "Success",
      data: res.data || [],
    };
  } catch (error) {
    console.error("❌ Fetch Tournaments Error:", error);

    return {
      success: false,
      message: error?.message || "Unexpected error occurred",
      data: [],
    };
  }
};


/* =============================================================
   📥 EXPORT PARTICIPANTS (STANDARDIZED - apiCall)
============================================================= */
export const exportParticipants = async (
  { tournament_id } = {},
  options = {}
) => {
  try {
    if (!tournament_id) {
      return {
        success: false,
        message: "Tournament ID is required",
        data: null,
      };
    }

    const blob = await apiCall(
      "GET",
     ENDPOINTS.EXPORTPARTICIPANTS,
      null,
      "Export Participants",
      {
        params: {
          tournament_id,
        },
        responseType: "blob", // 🔥 consistent with apiCall
        ...options,
      }
    );

    // ✅ safety check
    if (!blob || blob.size === 0) {
      throw new Error("Empty file received");
    }

    return {
      success: true,
      message: "Export successful",
      data: blob,
    };
  } catch (error) {
    return handleError(error, "Export Participants");
  }
};

/* =============================================================
   📄 DOWNLOAD RECEIPT (PDF)
============================================================= */
export const downloadReceipt = async (
  { participant_id } = {},
  options = {}
) => {
  try {
    if (!participant_id) {
      return {
        success: false,
        message: "Participant ID is required",
        data: null,
      };
    }

    const blob = await apiCall(
      "GET",
      ENDPOINTS.RECEIPT,
      null,
      "Download Receipt",
      {
        params: { participant_id },
        responseType: "blob", // 🔥 important
        ...options,
      }
    );

    if (!blob || blob.size === 0) {
      throw new Error("Empty receipt received");
    }

    return {
      success: true,
      message: "Receipt downloaded",
      data: blob,
    };
  } catch (error) {
    return handleError(error, "Download Receipt");
  }
};